/**
 * Meta Pixel wrapper for the web2app funnel.
 *
 * Browser tracking is enabled by REACT_APP_META_PIXEL_ID. Optional server
 * tracking uses the same event ID for deduplication. See META_ADS_SETUP.md.
 */

type MetaEventParams = Record<string, string | number | boolean | undefined>;

type MetaPixel = {
  (command: 'init', pixelId: string): void;
  (
    command: 'track' | 'trackCustom',
    eventName: string,
    params?: MetaEventParams,
    options?: { eventID: string }
  ): void;
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[][];
  push: MetaPixel;
  loaded: boolean;
  version: string;
};

declare global {
  interface Window {
    fbq?: MetaPixel;
    _fbq?: MetaPixel;
    globalPrivacyControl?: boolean;
  }
}

// Pixel IDs are public. An environment setting can override or disable this ID.
const pixelId = (process.env.REACT_APP_META_PIXEL_ID ?? '3760030694138123').trim();
const capiEnabled = process.env.REACT_APP_META_CAPI_ENABLED === 'true';
let initialized = false;
let lastPageView: string | undefined;

const trackingEnabled = () => /^\d+$/.test(pixelId) && !window.globalPrivacyControl;

const initializePixel = () => {
  if (initialized) return;
  if (!window.fbq) {
    const pixel = function (...args: unknown[]) {
      if (pixel.callMethod) pixel.callMethod(...args);
      else pixel.queue.push(args);
    } as MetaPixel;
    pixel.queue = [];
    pixel.push = pixel;
    pixel.loaded = true;
    pixel.version = '2.0';
    window.fbq = pixel;
    window._fbq = pixel;
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(script);
  }
  window.fbq('init', pixelId);
  initialized = true;
};

// Persisted so the fbclid from the ad click is still around at checkout, which
// can happen minutes later on a different route.
const FBCLID_KEY = 'uncling_fbclid';
const CAPI_ENDPOINT = '/api/meta-capi';

const newEventId = (): string => {
  try {
    return window.crypto.randomUUID();
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
};

const readCookie = (name: string): string | undefined => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : undefined;
};

/**
 * Meta's _fbc cookie is only written when the pixel sees fbclid on the landing
 * URL. Route changes lose the param, so we stash it and rebuild _fbc ourselves.
 */
export const captureFbclid = () => {
  if (!trackingEnabled()) return;
  try {
    const fbclid = new URLSearchParams(window.location.search).get('fbclid');
    if (fbclid) {
      window.localStorage.setItem(FBCLID_KEY, `fb.1.${Date.now()}.${fbclid}`);
    }
  } catch {
    // Storage unavailable; we fall back to the _fbc cookie alone.
  }
};

const readFbc = (): string | undefined => {
  const cookie = readCookie('_fbc');
  if (cookie) return cookie;
  try {
    return window.localStorage.getItem(FBCLID_KEY) ?? undefined;
  } catch {
    return undefined;
  }
};

type CapiUserData = {
  email?: string;
};

const sendToCapi = (
  eventName: string,
  eventId: string,
  params: MetaEventParams,
  userData: CapiUserData
) => {
  if (!capiEnabled) return;
  const sourceUrl = new URL(window.location.href);
  // Attribution uses fbc below; query strings can contain personal data.
  sourceUrl.search = '';
  sourceUrl.hash = '';
  const body = JSON.stringify({
    eventName,
    eventId,
    eventSourceUrl: sourceUrl.href,
    customData: params,
    userData: {
      // Hashed server-side — never send a raw email to the browser pixel.
      email: userData.email,
      fbp: readCookie('_fbp'),
      fbc: readFbc()
    }
  });

  try {
    // keepalive so the request survives the App Store navigation.
    void fetch(CAPI_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true
    }).catch(() => undefined);
  } catch {
    // Measurement must never block the funnel.
  }
};

const track = (
  kind: 'track' | 'trackCustom',
  eventName: string,
  params: MetaEventParams = {},
  userData: CapiUserData = {}
) => {
  if (!trackingEnabled()) return;
  const eventId = newEventId();

  try {
    initializePixel();
    window.fbq?.(kind, eventName, params, { eventID: eventId });
  } catch {
    // Pixel blocked; the CAPI call below still reports the event.
  }

  sendToCapi(eventName, eventId, params, userData);
};

export const trackMetaPageView = (path: string) => {
  if (!trackingEnabled() || path === lastPageView) return;
  lastPageView = path;
  // React StrictMode replays initial effects; count a route only once.
  track('track', 'PageView', { content_name: path.split(/[?#]/)[0] });
};

/** Fired when the intake questions start — the top of the measurable funnel. */
export const trackQuizStart = () => {
  track('trackCustom', 'QuizStart', { content_name: 'attachment_style_quiz' });
};

/** Fired when a completed quiz shows its calculated result. */
export const trackQuizComplete = (dominantStyle: string) => {
  track('trackCustom', 'QuizComplete', {
    content_name: 'attachment_style_quiz',
    attachment_style: dominantStyle
  });
};

/** Fired only after the guide request succeeds. */
export const trackLead = (email: string, dominantStyle: string) => {
  track('track', 'Lead', { content_name: 'attachment_guide', attachment_style: dominantStyle }, { email });
};

export const trackInitiateCheckout = (value: number, currency = 'USD') => {
  track('track', 'InitiateCheckout', { value, currency });
};

export const trackPurchase = (value: number, currency = 'USD', email?: string) => {
  track('track', 'Purchase', { value, currency }, { email });
};

/** App Store handoff. Not a conversion — it is the step you lose sight of, so
 *  it is worth watching as a drop-off rate, not optimizing toward. */
export const trackAppStoreClick = (source: string) => {
  track('trackCustom', 'AppStoreClick', { content_name: source });
};
